/**
 * QueryCache
 *
 * Generic in-memory TTL cache with in-flight request deduplication.
 *
 * Design:
 * - Cache key = full request URL (including query params), so distinct
 *   parameter combinations are cached independently.
 * - get() returns a cached Promise.resolve() when within TTL, otherwise
 *   calls the fetcher and caches the result.
 * - Concurrent calls for the same key share a single in-flight Promise,
 *   preventing duplicate network requests during a burst.
 * - invalidateByPrefix() clears all keys that start with a given path
 *   segment (e.g. '/analytics') so a single call resets a whole feature.
 * - clear() must be called on logout to prevent user data from leaking
 *   into the next session.
 *
 * Usage:
 *   // In an API module
 *   const url = `/analytics/profit-loss?${qs}`;
 *   return queryCache.get(url, () => apiClient.get(url), CACHE_TTL.ANALYTICS);
 *
 *   // To invalidate on mutation / refresh
 *   queryCache.invalidateByPrefix('/analytics');
 *
 *   // On logout
 *   queryCache.clear();
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number; // epoch ms
}

class QueryCache {
  private readonly store = new Map<string, CacheEntry<unknown>>();
  private readonly inFlight = new Map<string, Promise<unknown>>();

  /**
   * Return the cached response for `key` if still within TTL.
   * Otherwise invoke `fetcher`, cache its result, and return the Promise.
   * Concurrent callers for the same key share one in-flight Promise.
   */
  get<T>(key: string, fetcher: () => Promise<T>, ttl: number): Promise<T> {
    const cached = this.store.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return Promise.resolve(cached.data as T);
    }

    const inflight = this.inFlight.get(key);
    if (inflight) return inflight as Promise<T>;

    const promise = fetcher()
      .then((data) => {
        this.store.set(key, { data, expiresAt: Date.now() + ttl });
        this.inFlight.delete(key);
        return data;
      })
      .catch((err) => {
        // Do not cache errors — next call retries the network.
        this.inFlight.delete(key);
        throw err;
      });

    this.inFlight.set(key, promise);
    return promise;
  }

  /**
   * Invalidate all keys whose string starts with `prefix`.
   * Also removes any matching in-flight entries so the next caller
   * fires a fresh request rather than receiving a stale in-flight response.
   */
  invalidateByPrefix(prefix: string): void {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) this.store.delete(key);
    }
    for (const key of this.inFlight.keys()) {
      if (key.startsWith(prefix)) this.inFlight.delete(key);
    }
  }

  /** Invalidate one exact cache key. */
  invalidate(key: string): void {
    this.store.delete(key);
    this.inFlight.delete(key);
  }

  /**
   * Clear the entire cache.
   * Call this on user logout to prevent data from a previous session
   * from being served to the next user on the same browser.
   */
  clear(): void {
    this.store.clear();
    this.inFlight.clear();
  }
}

/** Singleton cache shared across the entire application. */
export const queryCache = new QueryCache();

/**
 * TTL (time-to-live) constants by data domain.
 *
 * Tune these based on how frequently the underlying data changes and
 * how much staleness is acceptable in the UI.
 */
export const CACHE_TTL = {
  /** Analytics aggregations — expensive server-side queries, changes slowly */
  ANALYTICS: 5 * 60 * 1000, // 5 minutes

  /** Inventory listings — moderate write frequency */
  INVENTORY: 2 * 60 * 1000, // 2 minutes

  /** Reference / lookup data (tags, units, storehouses) — rarely changes */
  REFERENCE: 5 * 60 * 1000, // 5 minutes
} as const;
